import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { mdiBellRingOutline, mdiEmailOutline, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";

import AutoComplete from "components/AutoComplete";
import Button from "components/Button";
import Checkbox from "components/Checkbox";
import Select from "components/Select";
import { EQUIPMENT_TYPES } from "constants/signup";
import { REGION_DATA } from "constants/trucks";
import { ALERT_FREQUENCY_LIST } from "constants/user-settings";
import { showSuccessMessage, showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { ShipperSettingsData, ShipperSettingsFormData } from "models/UserSetting";
import { updateShipperSettingsAPI } from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";

interface IShipperSettingsFormProps {
  defaultValue: ShipperSettingsData;
}

export default function ShipperSettingsForm({ defaultValue }: Readonly<IShipperSettingsFormProps>) {
  const { isDesktop } = useDeviceType();
  const dispatch = useDispatch<AppDispatch>();

  const { userDetails } = useSelector((state: RootState) => state.userState);

  const {
    register,
    control,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<Partial<ShipperSettingsFormData>>({
    values: {
      ...defaultValue,
      pickup_states: REGION_DATA.filter((region) =>
        defaultValue?.pickup_states.includes(region.value)
      ),
      equipment_type: EQUIPMENT_TYPES.filter((type) =>
        defaultValue?.equipment_type.includes(type.value)
      )
    }
  });

  const onSubmit = async (data: ShipperSettingsFormData) => {
    const {
      shipper_post_alerts,
      notify_shipper_post_alerts,
      equipment_type,
      pickup_states,
      alert
    } = data;

    const equipment_type_arr = equipment_type.map((type) => type.value);
    const pickup_states_arr = pickup_states.map((state) => state.value);

    const formData = {
      id: defaultValue.id,
      user: defaultValue.user,
      shipper_post_alerts,
      notify_shipper_post_alerts,
      equipment_type: equipment_type_arr,
      pickup_states: pickup_states_arr,
      alert
    } as ShipperSettingsData;

    dispatch(handleLoading(true));
    await updateShipperSettingsAPI(formData)
      .then(() => {
        showSuccessMessage("Settings updated successfully.");
      })
      .catch(() => {
        showErrorMessage("Error update shipper settings");
      })
      .finally(() => dispatch(handleLoading(false)));
  };

  return (
    <form className={styles.shipperSettingsForm} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.shipperLoadAlerts}>
        <div className={styles.header}>
          <span className={styles.title}>Shipper Load Alerts</span>
          <Icon path={mdiEmailOutline} className="size-5 desktop:size-6" />
          <Icon path={mdiBellRingOutline} className={styles.bellIcon} />
        </div>
        <div className={styles.alertBody}>
          <span className={styles.tableRow}>Alert Me When Shipper Posts New Load(s)</span>
          <Checkbox
            name="shipper_post_alerts"
            register={register}
            wrapperClass="-mr-1.5 desktop:-mr-0.5"
            {...(!userDetails?.has_verified_email && { disabled: true })}
          />
          <Checkbox
            name="notify_shipper_post_alerts"
            register={register}
            wrapperClass="-mr-1.5 desktop:-mr-0.5"
            {...(!userDetails?.has_verified_email && { disabled: true })}
          />
        </div>
      </div>

      <div className={styles.selectForm}>
        <AutoComplete
          name="pickup_states"
          label="&nbsp;&nbsp;OK Pickup States"
          placeholder="Any"
          options={REGION_DATA}
          control={control}
          className="flex-1"
          {...(!userDetails?.has_verified_email && { disabled: true })}
          isMulti
        />
        <AutoComplete
          name="equipment_type"
          label="&nbsp;&nbsp;OK Equipment Types"
          placeholder="Any"
          options={EQUIPMENT_TYPES}
          control={control}
          className="flex-1"
          isMulti
          {...(!userDetails?.has_verified_email && { disabled: true })}
        />
        <Controller
          name="alert"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              label="&nbsp;&nbsp;Alert Frequency"
              value={ALERT_FREQUENCY_LIST.find((item) => item.value === value)}
              options={ALERT_FREQUENCY_LIST}
              onChange={(alert) => onChange(alert.value)}
              className="flex-1"
              {...(!userDetails?.has_verified_email && { disabled: true })}
            />
          )}
        />

        <Button
          type="submit"
          variant={isDesktop ? "outline" : "contained"}
          className={styles.submit}
          disabled={!userDetails?.has_verified_email || isSubmitting}>
          {isSubmitting ? <Icon path={mdiLoading} size={1.5} spin={1} /> : "Save"}
        </Button>
      </div>
    </form>
  );
}
